import React, { useState, useEffect, useCallback } from 'react';
import { useSnackbar } from 'notistack';
import { FaPlus, FaTrash, FaEdit } from 'react-icons/fa';
import {
    Box, CircularProgress, Typography, Paper, Button, Tabs, Tab, Table, TableBody,
    TableCell, TableContainer, TableHead, TableRow, FormControl, Select, MenuItem,
    InputLabel, List, ListItem, ListItemText, Chip, OutlinedInput
} from '@mui/material';

import { getKumesler, deleteKumes, getUsers, getKullaniciKumesleri, saveKullaniciKumesSecimi, getUreticiBilgileri } from '../services/api';
import KumesDuzenle from '../components/KumesDuzenle';
import KumesModal from '../components/KumesModal';
import KumesTeknikBilgilerContent from '../components/KumesTeknikBilgilerContent';

const ORANGE_COLOR = '#FF7A2B';
const ORANGE_HOVER_COLOR = '#FF5F1F';
const LIGHT_ORANGE_EVEN = '#FFEFE7';
const LIGHT_ORANGE_HOVER = '#FFE5D9';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
    PaperProps: {
        style: {
            maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
            width: 250,
        },
    },
};

const KumeBilgileri = () => {
    const [aktifSekme, setAktifSekme] = useState(0);
    const [kumesler, setKumesler] = useState([]);
    const [kullanicilar, setKullanicilar] = useState([]);
    const [yukleniyor, setYukleniyor] = useState(false);
    const { enqueueSnackbar } = useSnackbar();

    const [duzenlenenKumesId, setDuzenlenenKumesId] = useState(null);
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);

    const [kullaniciSirketId, setKullaniciSirketId] = useState(null);

    useEffect(() => {
        const fetchKullaniciSirketId = async () => {
            try {
                const res = await getUreticiBilgileri();
                
                const sirketId = res.data?.data?.[0]?.ENTEGRE_SIRKET_ID;

                if (sirketId) {
                    setKullaniciSirketId(sirketId);
                } else {
                    enqueueSnackbar('Kullanıcıya ait entegre şirket ID bilgisi bulunamadı!', { variant: 'error' });
                }
            } catch (err) {
                enqueueSnackbar('Üretici bilgileri getirilirken bir hata oluştu: ' + err.message, { variant: 'error' });
            }
        };

        fetchKullaniciSirketId();
    }, [enqueueSnackbar]);


    const fetchKumesler = useCallback(async () => {
        setYukleniyor(true);
        try {
            const res = await getKumesler();
            setKumesler(res.data.data || []);
        } catch (err) {
            enqueueSnackbar('Kümesler getirilirken bir hata oluştu: ' + err.message, { variant: 'error' });
        } finally {
            setYukleniyor(false);
        }
    }, [enqueueSnackbar]);

    const fetchKullanicilarVeAtamalari = useCallback(async () => {
        setYukleniyor(true);
        try {
            const [kullaniciRes, kumesRes] = await Promise.all([getUsers(), getKumesler()]);
            const kullanicilarData = kullaniciRes.data?.data || [];
            const tumKumesler = kumesRes.data?.data || [];
            const kullaniciAtamaPromises = kullanicilarData.map(k => getKullaniciKumesleri(k.KULLANICI_ID));
            const atamaResults = await Promise.all(kullaniciAtamaPromises);
            const eslesmisKullanicilar = kullanicilarData.map((kullanici, index) => {
                const atamalar = atamaResults[index].data?.data;
                const atamalarArray = Array.isArray(atamalar) ? atamalar : (atamalar ? [atamalar] : []);
                const zenginlestirilmisAtamalar = atamalarArray.map(atama => {
                    const kumesDetayi = tumKumesler.find(k => k.KUMES_ID === atama.KUMES_ID);
                    return { ...atama, KUMES_ADI: kumesDetayi ? kumesDetayi.KUMES_ADI : `ID: ${atama.KUMES_ID}` };
                });
                return { ...kullanici, ATANAN_KUMESLER: zenginlestirilmisAtamalar };
            });
            setKumesler(tumKumesler);
            setKullanicilar(eslesmisKullanicilar);
        } catch (err) {
            enqueueSnackbar('Kullanıcı veya kümes verileri alınamadı: ' + err.message, { variant: 'error' });
        } finally {
            setYukleniyor(false);
        }
    }, [enqueueSnackbar]);

    useEffect(() => {
        if (!duzenlenenKumesId) {
            if (aktifSekme === 0) fetchKumesler();
            else fetchKullanicilarVeAtamalari();
        }
    }, [aktifSekme, duzenlenenKumesId, fetchKumesler, fetchKullanicilarVeAtamalari]);

    const handleSekmeDegistir = (event, yeniDeger) => setAktifSekme(yeniDeger);
    const handleDuzenleAc = (kumesId) => setDuzenlenenKumesId(kumesId);
    const handleYeniEkleAc = () => setIsAddModalOpen(true);
    const handleModalKapat = () => setIsAddModalOpen(false);
    const handleListeDon = () => setDuzenlenenKumesId(null);
    const handleKayitBasarili = () => {
        setIsAddModalOpen(false);
        setDuzenlenenKumesId(null);
        if (aktifSekme === 0) fetchKumesler();
        else fetchKullanicilarVeAtamalari();
    };
    const handleKumesSil = async (id) => {
        if (window.confirm('Bu kümesi silmek istediğinize emin misiniz?')) {
            try {
                await deleteKumes(id);
                enqueueSnackbar('Kümes başarıyla silindi.', { variant: 'success' });
                fetchKumesler();
            } catch (err) {
                enqueueSnackbar('Kümes silinirken bir hata oluştu: ' + err.message, { variant: 'error' });
            }
        }
    };
    const handleKumesKaldir = async (kullanici, atamaToRemove) => {
        
        if (!window.confirm(`'${kullanici.KULLANICI_ADI}' kullanıcısından '${atamaToRemove.KUMES_ADI}' kümesini kaldırmak istediğinizden emin misiniz?`)) { return; }
        const originalKullanicilar = [...kullanicilar];
        const yeniKullanicilar = kullanicilar.map(u => u.KULLANICI_ID === kullanici.KULLANICI_ID ? { ...u, ATANAN_KUMESLER: u.ATANAN_KUMESLER.filter(a => a.KULLANICI_KUMES_ID !== atamaToRemove.KULLANICI_KUMES_ID) } : u);
        setKullanicilar(yeniKullanicilar);
        try {
            await saveKullaniciKumesSecimi({ kullaniciId: kullanici.KULLANICI_ID, kumesId: atamaToRemove.KUMES_ID, kullaniciKumesId: atamaToRemove.KULLANICI_KUMES_ID });
            enqueueSnackbar('Kümes ataması başarıyla kaldırıldı.', { variant: 'success' });
            fetchKullanicilarVeAtamalari();
        } catch (error) {
            enqueueSnackbar('Atama kaldırılırken bir hata oluştu: ' + error.message, { variant: 'error' });
            setKullanicilar(originalKullanicilar);
        }
    };
    const handleKumesSecimiDegisikligi = async (kullanici, yeniSecilenKumesIdleri) => {
        const originalKullanicilar = [...kullanicilar];
        const eskiAtamalar = kullanici.ATANAN_KUMESLER || [];
        const eskiKumesIdleri = eskiAtamalar.map(a => a.KUMES_ID);
        const guncelKullaniciAtamalari = kumesler.filter(k => yeniSecilenKumesIdleri.includes(k.KUMES_ID)).map(k => {
            const mevcutAtama = eskiAtamalar.find(a => a.KUMES_ID === k.KUMES_ID);
            return mevcutAtama || { KUMES_ID: k.KUMES_ID, KUMES_ADI: k.KUMES_ADI, KULLANICI_KUMES_ID: `temp-${k.KUMES_ID}` };
        });
        const yeniKullanicilarState = kullanicilar.map(u => u.KULLANICI_ID === kullanici.KULLANICI_ID ? { ...u, ATANAN_KUMESLER: guncelKullaniciAtamalari } : u);
        setKullanicilar(yeniKullanicilarState);
        const eklenecekIdler = yeniSecilenKumesIdleri.filter(id => !eskiKumesIdleri.includes(id));
        const kaldirilacakAtamalar = eskiAtamalar.filter(a => !yeniSecilenKumesIdleri.includes(a.KUMES_ID));
        const promises = [];
        eklenecekIdler.forEach(kumesId => { promises.push(saveKullaniciKumesSecimi({ kullaniciId: kullanici.KULLANICI_ID, kumesId, kullaniciKumesId: 0 })); });
        kaldirilacakAtamalar.forEach(atama => { promises.push(saveKullaniciKumesSecimi({ kullaniciId: kullanici.KULLANICI_ID, kumesId: atama.KUMES_ID, kullaniciKumesId: atama.KULLANICI_KUMES_ID })); });
        if (promises.length === 0) return;
        try {
            await Promise.all(promises);
            enqueueSnackbar('Kullanıcı kümes atamaları başarıyla güncellendi.', { variant: 'success' });
        } catch (error) {
            enqueueSnackbar('Atamalar kaydedilirken bir hata oluştu: ' + error.message, { variant: 'error' });
            setKullanicilar(originalKullanicilar);
        } finally {
            fetchKullanicilarVeAtamalari();
        }
    };


    if (duzenlenenKumesId) {
        return <KumesDuzenle kumesId={duzenlenenKumesId} onSaveSuccess={handleKayitBasarili} onCancel={handleListeDon} />;
    }

    return (
        <Paper sx={{ p: { xs: 2, sm: 3 }, margin: 2, borderRadius: 2, boxShadow: 3 }}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs value={aktifSekme} onChange={handleSekmeDegistir} sx={{ '& .Mui-selected': { color: ORANGE_COLOR, fontWeight: 'bold' }, '& .MuiTabs-indicator': { backgroundColor: ORANGE_COLOR } }}>
                    <Tab label="Kümes Bilgileri" id="kumes-tab-0" />
                    <Tab label="Kümes Kullanıcı Eşleşmesi" id="kumes-tab-1" />
                </Tabs>
            </Box>

                {aktifSekme === 0 && (
                    <Box sx={{ mt: 3 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                            {/* <Button
                                variant="contained"
                                startIcon={<FaPlus />}
                                onClick={handleYeniEkleAc}
                                disabled={!kullaniciSirketId}
                                sx={{
                                    backgroundColor: ORANGE_COLOR,
                                    '&:hover': { backgroundColor: ORANGE_HOVER_COLOR }
                                }}
                            >
                                Yeni Kümes Ekle
                            </Button> */}
                        </Box>

                        <KumesTeknikBilgilerContent />
                    </Box>
                )}

            {aktifSekme === 1 && (
                <Box sx={{ mt: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: '600', borderLeft: `4px solid ${ORANGE_COLOR}`, pl: 1.5 }}>Kullanıcılara Kümes Ata</Typography>
                    {yukleniyor ? <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}><CircularProgress sx={{ color: ORANGE_COLOR }} /></Box> : (
                        <Paper variant="outlined">
                            <List disablePadding>
                                {kullanicilar.map((kullanici) => {
                                    const atanmisKumesIdleri = kullanici.ATANAN_KUMESLER.map(k => k.KUMES_ID);
                                    return (
                                        <ListItem key={kullanici.KULLANICI_ID} divider sx={{ alignItems: 'flex-start', pt: 2, pb: 2, '&:hover': { backgroundColor: LIGHT_ORANGE_EVEN }, display: 'flex', flexWrap: 'wrap' }}>
                                            <Box sx={{ flexGrow: 1, pr: 2, minWidth: '250px', mb: { xs: 2, sm: 0 } }}>
                                                <ListItemText primaryTypographyProps={{ fontWeight: 'bold' }} primary={`${kullanici.KULLANICI_ADI} ${kullanici.KULLANICI_SOYADI}`} secondary={kullanici.KULLANICI_EMAIL} />
                                                <Box sx={{ mt: 1.5 }}>
                                                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold', display: 'block', mb: 0.5 }}>MEVCUT ATAMALAR:</Typography>
                                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                        {kullanici.ATANAN_KUMESLER.length > 0 ? (
                                                            kullanici.ATANAN_KUMESLER.map(atama => {
                                                                const uniqueKey = atama.KULLANICI_KUMES_ID && atama.KULLANICI_KUMES_ID !== 0 ? atama.KULLANICI_KUMES_ID : `kullanici-${kullanici.KULLANICI_ID}-kumes-${atama.KUMES_ID}`;
                                                                return <Chip key={uniqueKey} label={atama.KUMES_ADI} size="small" variant="outlined" onDelete={() => handleKumesKaldir(kullanici, atama)} />;
                                                            })
                                                        ) : <Typography variant="caption" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>Atanmış kümes yok.</Typography>}
                                                    </Box>
                                                </Box>
                                            </Box>
                                            <FormControl sx={{ width: { xs: '100%', sm: 300 }, flexShrink: 0 }} size="small">
                                                <InputLabel id={`select-kumes-label-${kullanici.KULLANICI_ID}`} sx={{ '&.Mui-focused': { color: ORANGE_COLOR } }}>Yeni Atama Yap / Düzenle</InputLabel>
                                                <Select
                                                    labelId={`select-kumes-label-${kullanici.KULLANICI_ID}`}
                                                    multiple
                                                    value={atanmisKumesIdleri}
                                                    onChange={(e) => handleKumesSecimiDegisikligi(kullanici, e.target.value)}
                                                    input={<OutlinedInput label="Yeni Atama Yap / Düzenle" />}
                                                    renderValue={(selectedIds) => <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>{selectedIds.map(id => { const kumes = kumesler.find(k => k.KUMES_ID === id); return <Chip key={id} label={kumes ? kumes.KUMES_ADI : id} size="small" />; })}</Box>}
                                                    MenuProps={MenuProps}
                                                    sx={{ '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: ORANGE_COLOR }, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: ORANGE_COLOR } }}
                                                >
                                                    {kumesler.map(kumes => <MenuItem key={kumes.KUMES_ID} value={kumes.KUMES_ID}>{kumes.KUMES_ADI}</MenuItem>)}
                                                </Select>
                                            </FormControl>
                                        </ListItem>
                                    );
                                })}
                            </List>
                        </Paper>
                    )}
                </Box>
            )}

            <KumesModal
                open={isAddModalOpen}
                handleClose={handleModalKapat}
                onSave={handleKayitBasarili}
                sirketId={kullaniciSirketId}
            />
        </Paper>
    );
};

export default KumeBilgileri;